import { useState, useEffect, useCallback } from 'react';
import type { ProfileVertical } from '@photofeeler/shared';

/**
 * Animated AI scanning demo for the landing page hero.
 * Shows a photo being analyzed with bounding boxes, detection labels,
 * scan line, and a score counter — loops continuously.
 *
 * Accepts an optional `vertical` prop to display vertical-specific
 * traits and coaching copy (Dating vs LinkedIn). Defaults to 'dating'.
 */

interface Detection {
  /** Label shown beside the bounding box */
  label: string;
  /** Value / assessment */
  value: string;
  /** Color class for the accent */
  color: string;
  /** Bounding box position as percentages [top, left, width, height] */
  box: [number, number, number, number];
}

const DATING_DETECTIONS: Detection[] = [
  { label: 'Face',       value: 'Detected',      color: '#4AA88A', box: [12, 22, 56, 65] },
  { label: 'Smile',      value: 'Warm, genuine',  color: '#D4736A', box: [42, 30, 40, 22] },
  { label: 'Eye contact', value: 'Direct gaze',   color: '#5B8EC9', box: [28, 30, 40, 14] },
  { label: 'Energy',     value: 'Approachable',   color: '#C9944A', box: [4, 60, 36, 30] },
  { label: 'Vibe',       value: 'Natural',        color: '#4AA88A', box: [2, 2, 96, 96] },
];

const LINKEDIN_DETECTIONS: Detection[] = [
  { label: 'Face',       value: 'Detected',      color: '#4AA88A', box: [12, 22, 56, 65] },
  { label: 'Composure',  value: 'Confident',      color: '#5B8EC9', box: [42, 30, 40, 22] },
  { label: 'Clarity',    value: 'Sharp focus',    color: '#5D7FA3', box: [28, 30, 40, 14] },
  { label: 'Authority',  value: 'Professional',   color: '#4AA88A', box: [4, 60, 36, 30] },
  { label: 'Polish',     value: 'Recruiter-ready', color: '#5D7FA3', box: [2, 2, 96, 96] },
];

const VERTICAL_DETECTIONS: Record<ProfileVertical, Detection[]> = {
  dating: DATING_DETECTIONS,
  linkedin: LINKEDIN_DETECTIONS,
};

interface VerticalDemoConfig {
  label: string;
  traits: { name: string; target: number; pct: number }[];
  coachingSummary: string;
}

const VERTICAL_CONFIGS: Record<ProfileVertical, VerticalDemoConfig> = {
  dating: {
    label: 'Dating',
    traits: [
      { name: 'Attractiveness', target: 8.4, pct: 84 },
      { name: 'Trustworthiness', target: 8.1, pct: 81 },
      { name: 'Likability', target: 7.8, pct: 78 },
    ],
    coachingSummary: 'Great eye contact and warm, genuine smile',
  },
  linkedin: {
    label: 'LinkedIn',
    traits: [
      { name: 'Competence', target: 8.6, pct: 86 },
      { name: 'Influence', target: 7.9, pct: 79 },
      { name: 'Approachability', target: 8.2, pct: 82 },
    ],
    coachingSummary: 'Clean background, confident professional look',
  },
};

/** Different hero photos per vertical — the image teaches the category faster than copy */
const VERTICAL_PHOTOS: Record<ProfileVertical, string> = {
  // Dating: warm, casual, social — smiling woman with natural light
  dating: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=face&q=80',
  // LinkedIn: clean, professional headshot — business attire, neutral background
  linkedin: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop&crop=face&q=80',
};

const CYCLE_MS = 6000; // Total animation cycle
const DETECTION_INTERVAL = 700; // Time between each detection appearing
const SCORE_START_DELAY = 3500; // When score starts counting
const SCORE_DURATION = 1200; // How long the count-up takes
const PAUSE_BEFORE_RESET = 1200; // Pause showing final state before reset

interface HeroDemoProps {
  vertical?: ProfileVertical;
}

export function HeroDemo({ vertical = 'dating' }: HeroDemoProps) {
  const config = VERTICAL_CONFIGS[vertical];
  const detections = VERTICAL_DETECTIONS[vertical];
  const photoUrl = VERTICAL_PHOTOS[vertical];
  const [phase, setPhase] = useState<'scanning' | 'scoring' | 'done'>('scanning');
  const [visibleCount, setVisibleCount] = useState(0);
  const [score, setScore] = useState(0);
  const [scanProgress, setScanProgress] = useState(0);

  const reset = useCallback(() => {
    setPhase('scanning');
    setVisibleCount(0);
    setScore(0);
    setScanProgress(0);
  }, []);

  // Main cycle controller
  useEffect(() => {
    let cancelled = false;

    // Phase 1: Reveal detections one by one
    const detectionTimers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < detections.length; i++) {
      detectionTimers.push(
        setTimeout(() => {
          if (!cancelled) setVisibleCount(i + 1);
        }, i * DETECTION_INTERVAL)
      );
    }

    // Scan progress (0 → 100 over first 3.5s)
    const scanStart = performance.now();
    let scanFrame: number;
    const animateScan = (now: number) => {
      if (cancelled) return;
      const elapsed = now - scanStart;
      const progress = Math.min(elapsed / SCORE_START_DELAY, 1);
      setScanProgress(progress * 100);
      if (progress < 1) {
        scanFrame = requestAnimationFrame(animateScan);
      }
    };
    scanFrame = requestAnimationFrame(animateScan);

    // Phase 2: Score count-up
    const scoreTimer = setTimeout(() => {
      if (cancelled) return;
      setPhase('scoring');
      const target = 84;
      const start = performance.now();
      let frame: number;
      const animateScore = (now: number) => {
        if (cancelled) return;
        const elapsed = now - start;
        const progress = Math.min(elapsed / SCORE_DURATION, 1);
        // Ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setScore(Math.round(eased * target));
        if (progress < 1) {
          frame = requestAnimationFrame(animateScore);
        } else {
          setPhase('done');
        }
      };
      frame = requestAnimationFrame(animateScore);
      return () => cancelAnimationFrame(frame);
    }, SCORE_START_DELAY);

    // Phase 3: Reset and loop
    const resetTimer = setTimeout(() => {
      if (!cancelled) reset();
    }, CYCLE_MS);

    return () => {
      cancelled = true;
      detectionTimers.forEach(clearTimeout);
      clearTimeout(scoreTimer);
      clearTimeout(resetTimer);
      cancelAnimationFrame(scanFrame);
    };
  }, [reset]);

  // Re-trigger cycle after reset
  useEffect(() => {
    if (phase === 'scanning' && visibleCount === 0 && score === 0) {
      // This is handled by the main effect above re-running when reset changes state
    }
  }, [phase, visibleCount, score]);

  // Auto-loop: when cycle ends, schedule restart
  useEffect(() => {
    if (phase !== 'done') return;
    const timer = setTimeout(reset, PAUSE_BEFORE_RESET);
    return () => clearTimeout(timer);
  }, [phase, reset]);

  return (
    <div className="card p-5 max-w-sm mx-auto select-none">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">
            Live demo
          </div>
          <div className="font-serif text-xl text-ink" style={{ fontWeight: 600 }}>
            Multi-model photo audit
          </div>
        </div>
        <div className="hero-chip py-1.5 px-3 text-[11px]">
          3 model consensus
        </div>
      </div>
      {/* Photo with overlays */}
      <div className="relative overflow-hidden rounded-xl mb-4" style={{ aspectRatio: '4/3' }}>
        <img
          src={photoUrl}
          alt="Sample photo being analyzed"
          className="w-full h-full object-cover"
          loading="eager"
        />

        {/* Scan line — sweeps top to bottom */}
        <div
          className="absolute left-0 right-0 h-1 pointer-events-none transition-none"
          style={{
            top: `${scanProgress}%`,
            opacity: phase === 'scanning' ? 0.9 : 0,
            background: 'linear-gradient(to bottom, transparent, rgba(212,115,106,0.6), transparent)',
            height: '12px',
            boxShadow: '0 0 20px 4px rgba(212,115,106,0.3)',
            transition: 'opacity 0.4s ease',
          }}
        />

        {/* Scan overlay gradient (darkens area above scan line) */}
        {phase === 'scanning' && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, rgba(212,115,106,0.04) ${scanProgress}%, transparent ${scanProgress}%)`,
            }}
          />
        )}

        {/* Bounding boxes + labels */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full pointer-events-none"
          preserveAspectRatio="none"
        >
          {detections.slice(0, visibleCount).map((det, i) => {
            const [top, left, width, height] = det.box;
            const isLatest = i === visibleCount - 1 && phase === 'scanning';
            return (
              <g key={det.label}>
                {/* Corner brackets instead of full box for modern look */}
                <rect
                  x={left}
                  y={top}
                  width={width}
                  height={height}
                  fill="none"
                  stroke={det.color}
                  strokeWidth={isLatest ? 0.7 : 0.4}
                  strokeDasharray={`${Math.min(width, height) * 0.2} ${(width + height) * 2 - Math.min(width, height) * 0.8}`}
                  strokeDashoffset={0}
                  opacity={isLatest ? 1 : 0.5}
                  rx={1}
                  ry={1}
                  style={{
                    animation: isLatest ? 'none' : undefined,
                  }}
                >
                  {isLatest && (
                    <animate
                      attributeName="opacity"
                      values="0.6;1;0.6"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  )}
                </rect>
              </g>
            );
          })}
        </svg>

        {/* Detection label badges — positioned relative to their boxes */}
        {detections.slice(0, visibleCount).map((det, i) => {
          const [top, left, width] = det.box;
          const isLatest = i === visibleCount - 1 && phase === 'scanning';
          return (
            <div
              key={det.label}
              className="absolute flex items-center gap-1 animate-scale-in"
              style={{
                top: `${Math.max(top - 1, 1)}%`,
                left: `${left + width * 0.05}%`,
                transform: 'translateY(-100%)',
                animationDelay: `${i * 0.05}s`,
              }}
            >
              <div
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-white backdrop-blur-sm"
                style={{
                  fontSize: '9px',
                  fontWeight: 500,
                  backgroundColor: `${det.color}${isLatest ? 'dd' : '99'}`,
                  lineHeight: 1.2,
                  letterSpacing: '0.02em',
                }}
              >
                <span style={{ opacity: 0.8 }}>{det.label}</span>
                <span style={{ fontWeight: 600 }}>{det.value}</span>
              </div>
            </div>
          );
        })}

        {/* "Analyzing..." status badge */}
        {phase === 'scanning' && (
          <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 bg-ink/70 backdrop-blur-sm rounded-md px-2.5 py-1.5">
            <div className="w-2 h-2 rounded-full bg-rose animate-pulse" />
            <span className="text-[10px] text-white/90 font-sans font-medium tracking-wide">
              Analyzing{visibleCount < detections.length ? '...' : ' complete'}
            </span>
            <div className="flex-1" />
            <span className="text-[10px] text-white/50 font-sans tabular-nums">
              {Math.round(scanProgress)}%
            </span>
          </div>
        )}
      </div>

      {/* Score + traits area */}
      <div className="px-1">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-sans text-ink-muted font-medium">{config.label}</span>
          <div className="flex items-baseline gap-1">
            <span
              className="font-sans tabular-nums transition-colors duration-300"
              style={{
                fontSize: phase === 'scanning' ? '16px' : '24px',
                fontWeight: 700,
                color: phase === 'scanning' ? '#999' : '#4AA88A',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {phase === 'scanning' ? '—' : score}
            </span>
            {phase !== 'scanning' && (
              <span className="text-[10px] text-ink-faint font-sans">/100</span>
            )}
          </div>
        </div>

        {/* Trait bars — animate when scoring starts */}
        <div className="space-y-1.5">
          {config.traits.map((trait) => (
            <div key={trait.name} className="flex items-center gap-2">
              <span className="text-[11px] text-ink-muted w-24" style={{ fontWeight: 400 }}>{trait.name}</span>
              <div className="flex-1 h-1 bg-cream-dark rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: phase === 'scanning' ? '0%' : `${trait.pct}%`,
                    backgroundColor: '#4AA88A',
                    transition: 'width 1s cubic-bezier(0.16, 1, 0.3, 1)',
                    transitionDelay: phase !== 'scanning' ? '0.2s' : '0s',
                  }}
                />
              </div>
              <span
                className="text-[11px] w-6 text-right tabular-nums"
                style={{
                  fontWeight: 500,
                  color: phase === 'scanning' ? '#ccc' : '#6B6B6B',
                  transition: 'color 0.3s ease',
                }}
              >
                {phase === 'scanning' ? '—' : trait.target.toFixed(1)}
              </span>
            </div>
          ))}
        </div>

        {/* Coaching preview — appears after scoring */}
        <div
          className="mt-3 flex items-start gap-2 text-xs text-ink-muted overflow-hidden"
          style={{
            maxHeight: phase === 'done' ? '40px' : '0px',
            opacity: phase === 'done' ? 1 : 0,
            transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="text-success mt-0.5 shrink-0">&#10003;</span>
          <span>{config.coachingSummary}</span>
        </div>
      </div>
    </div>
  );
}
